const AUTH_TOKEN_KEY = 'authToken';
const AUTH_USER_KEY = 'authUser';
const DEFAULT_EXPIRY_SKEW_MS = 7_500;
const MINUTE_MS = 60 * 1000;
const DEFAULT_IDLE_TIMEOUT_MS = 60 * MINUTE_MS;
const DEFAULT_IDLE_WARNING_MS = 50 * MINUTE_MS;
const DEBUG_SHORT_TOKEN_EXPIRY_MS = 60_000;
export const AUTH_SESSION_WARNING_EVENT = 'sumrise:auth-session-warning';
export const AUTH_SESSION_EXPIRED_EVENT = 'sumrise:auth-session-expired';
export const AUTH_SESSION_ACTIVITY_EVENT = 'sumrise:auth-session-activity';

let autoLogoutTimeoutId = null;
let expiryWarningTimeoutId = null;
let absoluteExpiryTimeoutId = null;
let activityListenersInstalled = false;
let activeSessionCallbacks = null;
let redirectInProgress = false;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'popstate'];

function logDevAutoLogoutFromExpiryTimer() {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  const path = typeof window !== 'undefined' ? window.location?.pathname || '' : '';
  if (path) {
    console.info(`[auth] auto-logout triggered (expiry) path=${path}`);
    return;
  }
  console.info('[auth] auto-logout triggered (expiry)');
}

function decodeBase64Url(segment) {
  if (!segment || typeof segment !== 'string') {
    return null;
  }
  try {
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
}

function isDebugShortExpiryEnabled() {
  if (process.env.NODE_ENV === 'production') {
    return false;
  }
  const raw =
    process.env.REACT_APP_DEBUG_SHORT_TOKEN_EXPIRY ?? process.env.VITE_DEBUG_SHORT_TOKEN_EXPIRY;
  if (raw == null) {
    return false;
  }
  return ['1', 'true', 'yes', 'on'].includes(String(raw).trim().toLowerCase());
}

function clearAutoLogoutTimer() {
  if (autoLogoutTimeoutId !== null) {
    clearTimeout(autoLogoutTimeoutId);
    autoLogoutTimeoutId = null;
  }
  if (expiryWarningTimeoutId !== null) {
    clearTimeout(expiryWarningTimeoutId);
    expiryWarningTimeoutId = null;
  }
  if (absoluteExpiryTimeoutId !== null) {
    clearTimeout(absoluteExpiryTimeoutId);
    absoluteExpiryTimeoutId = null;
  }
}

function readMinutesConfig(name, fallbackMs) {
  const value = Number(process.env[name]);
  return Number.isFinite(value) && value > 0 ? value * MINUTE_MS : fallbackMs;
}

export function getIdleSessionConfig() {
  const timeoutMs = readMinutesConfig(
    'REACT_APP_SESSION_IDLE_TIMEOUT_MINUTES',
    DEFAULT_IDLE_TIMEOUT_MS
  );
  const warningMs = readMinutesConfig(
    'REACT_APP_SESSION_IDLE_WARNING_MINUTES',
    DEFAULT_IDLE_WARNING_MS
  );
  return {
    timeoutMs,
    warningMs: warningMs < timeoutMs ? warningMs : Math.max(0, timeoutMs - 10 * MINUTE_MS),
  };
}

export function notifySessionWarning(expMs) {
  window?.dispatchEvent?.(new CustomEvent(AUTH_SESSION_WARNING_EVENT, { detail: { expMs } }));
}

export function notifySessionExpired() {
  window?.dispatchEvent?.(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
}

export function isTokenExpired(token, nowMs = Date.now()) {
  const expMs = parseJwtExp(token);
  return expMs != null && expMs <= nowMs;
}

function getEffectiveExpMs(token, nowMs) {
  const parsedExpMs = parseJwtExp(token);
  if (parsedExpMs == null) {
    return null;
  }
  if (isDebugShortExpiryEnabled()) {
    return nowMs + DEBUG_SHORT_TOKEN_EXPIRY_MS;
  }
  return parsedExpMs;
}

export function parseJwtExp(token) {
  if (!token || typeof token !== 'string') {
    return null;
  }
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }
  const payload = decodeBase64Url(parts[1]);
  const expSeconds = payload?.exp;
  if (!Number.isFinite(expSeconds)) {
    return null;
  }
  return Math.trunc(expSeconds * 1000);
}

export function getTokenExpiryDelayMs(expMs, nowMs = Date.now(), skewMs = DEFAULT_EXPIRY_SKEW_MS) {
  if (!Number.isFinite(expMs)) {
    return null;
  }
  if (!Number.isFinite(nowMs) || !Number.isFinite(skewMs)) {
    return null;
  }
  return Math.trunc(expMs - nowMs - skewMs);
}

export function shouldLogoutNow(expMs, nowMs = Date.now(), skewMs = DEFAULT_EXPIRY_SKEW_MS) {
  const delayMs = getTokenExpiryDelayMs(expMs, nowMs, skewMs);
  return delayMs == null || delayMs <= 0;
}

export function resetLogoutRedirectGuard() {
  redirectInProgress = false;
}

export function clearAuth() {
  clearAutoLogoutTimer();
  activeSessionCallbacks = null;
  removeActivityListeners();
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
}

export function recordSessionActivity() {
  if (!activeSessionCallbacks) return;
  const { token, onLogout, onWarning, timeoutMs, warningMs } = activeSessionCallbacks;
  scheduleAutoLogout(token, onLogout, {
    onWarning,
    idleTimeoutMs: timeoutMs,
    idleWarningMs: warningMs,
    preserveAbsoluteTimer: true,
  });
  window?.dispatchEvent?.(new CustomEvent(AUTH_SESSION_ACTIVITY_EVENT));
}

function handleUserActivity() {
  recordSessionActivity();
}

function installActivityListeners() {
  if (activityListenersInstalled || typeof window === 'undefined') return;
  ACTIVITY_EVENTS.forEach((eventName) => window.addEventListener(eventName, handleUserActivity, true));
  activityListenersInstalled = true;
}

function removeActivityListeners() {
  if (!activityListenersInstalled || typeof window === 'undefined') return;
  ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, handleUserActivity, true));
  activityListenersInstalled = false;
}

function expireTrackedSession(onLogout) {
  clearAutoLogoutTimer();
  activeSessionCallbacks = null;
  removeActivityListeners();
  logDevAutoLogoutFromExpiryTimer();
  onLogout();
}

export function getSafeNextPath(nextPath) {
  if (typeof nextPath !== 'string') {
    return null;
  }
  const normalized = nextPath.trim();
  if (!normalized.startsWith('/') || normalized.startsWith('//')) {
    return null;
  }
  return normalized;
}

export function getLogoutRedirectPath({ reason, previousUserId, nextPath } = {}) {
  const query = new URLSearchParams();
  if (reason) {
    query.set('reason', reason);
  }
  if (reason && previousUserId != null && nextPath && nextPath !== '/login') {
    query.set('next', nextPath);
    query.set('next_user_id', String(previousUserId));
  }
  const queryString = query.toString();
  return queryString ? `/login?${queryString}` : '/login';
}

export function logoutAndRedirect({ reason, replaceLocation } = {}) {
  const previousUserId = (() => {
    try {
      return JSON.parse(localStorage.getItem(AUTH_USER_KEY))?.id ?? null;
    } catch (error) {
      return null;
    }
  })();
  clearAuth();
  if (typeof window === 'undefined') {
    return;
  }
  const pathname = window.location?.pathname || '';
  const search = window.location?.search || '';
  const hash = window.location?.hash || '';
  if (pathname === '/login' || redirectInProgress) {
    return;
  }
  redirectInProgress = true;
  const nextPath = `${pathname}${search}${hash}`;
  const replace = replaceLocation || ((path) => window.location.replace(path));
  replace(getLogoutRedirectPath({ reason, previousUserId, nextPath }));
}

// Manual test (dev-only): set REACT_APP_DEBUG_SHORT_TOKEN_EXPIRY=true, sign in,
// then verify auto-logout includes the current route and user ID in ~60 seconds.
export function scheduleAutoLogout(
  token,
  onLogout,
  {
    nowMs = Date.now(),
    skewMs,
    onWarning,
    idleTimeoutMs,
    idleWarningMs,
    preserveAbsoluteTimer = false,
  } = {}
) {
  if (preserveAbsoluteTimer) {
    if (autoLogoutTimeoutId !== null) clearTimeout(autoLogoutTimeoutId);
    if (expiryWarningTimeoutId !== null) clearTimeout(expiryWarningTimeoutId);
  } else {
    clearAutoLogoutTimer();
  }
  if (!token || typeof onLogout !== 'function') {
    activeSessionCallbacks = null;
    removeActivityListeners();
    return { scheduled: false, expMs: null, delayMs: null };
  }
  const expMs = getEffectiveExpMs(token, nowMs);
  if (expMs == null) {
    activeSessionCallbacks = null;
    removeActivityListeners();
    return { scheduled: false, expMs: null, delayMs: null };
  }
  const effectiveSkewMs = Number.isFinite(skewMs) ? skewMs : DEFAULT_EXPIRY_SKEW_MS;
  const absoluteDelayMs = getTokenExpiryDelayMs(expMs, nowMs, effectiveSkewMs);
  if (absoluteDelayMs == null || absoluteDelayMs <= 0) {
    expireTrackedSession(onLogout);
    return { scheduled: false, expMs, delayMs: 0 };
  }
  const config = getIdleSessionConfig();
  const timeoutMs = Number.isFinite(idleTimeoutMs) ? idleTimeoutMs : config.timeoutMs;
  const warningMs = Number.isFinite(idleWarningMs) ? idleWarningMs : config.warningMs;
  const warningDelayMs = Math.min(warningMs, timeoutMs);
  if (typeof onWarning === 'function') {
    expiryWarningTimeoutId = setTimeout(() => {
      expiryWarningTimeoutId = null;
      onWarning(nowMs + timeoutMs);
    }, warningDelayMs);
  }
  autoLogoutTimeoutId = setTimeout(() => {
    expireTrackedSession(onLogout);
  }, timeoutMs);
  if (!preserveAbsoluteTimer) {
    absoluteExpiryTimeoutId = setTimeout(() => {
      expireTrackedSession(onLogout);
    }, absoluteDelayMs);
  }
  activeSessionCallbacks = { token, onLogout, onWarning, timeoutMs, warningMs };
  installActivityListeners();
  return { scheduled: true, expMs, delayMs: timeoutMs, absoluteDelayMs };
}

export function scheduleAutoLogoutFromStorage(onLogout, onWarning) {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (!token) {
    clearAutoLogoutTimer();
    activeSessionCallbacks = null;
    removeActivityListeners();
    return { scheduled: false, expMs: null, delayMs: null };
  }
  return scheduleAutoLogout(token, onLogout, { onWarning });
}
